import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { API } from '@/App';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Globe, Shield } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
  open:         { label: 'Open',        cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  waitlist:     { label: 'Waitlist',    cls: 'bg-amber-50 text-amber-800 border-amber-200' },
  unavailable:  { label: 'Unavailable', cls: 'bg-rose-50 text-rose-800 border-rose-200' },
};

export default function SupportedCountries() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [busyIso, setBusyIso] = useState(null);

  const isPlatformAdmin = user?.role === 'platform_admin';

  useEffect(() => { fetchRows(); }, []);

  const fetchRows = async () => {
    try {
      const r = await fetch(`${API}/platform/supported-countries`, { credentials: 'include' });
      const data = await r.json();
      setRows(data.rows || []);
    } catch (e) {
      toast.error('Could not load Supported Countries');
    } finally {
      setLoading(false);
    }
  };

  const changeStatus = async (iso, next, note) => {
    setBusyIso(iso);
    try {
      const r = await fetch(`${API}/platform/supported-countries/${iso}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: next, note: note || null }),
      });
      if (!r.ok) throw new Error();
      const doc = await r.json();
      setRows(prev => prev.map(x => x.iso_code === iso ? doc : x));
      toast.success(`${doc.name} → ${STATUS_META[next].label}`);
    } catch (e) {
      toast.error('Could not update status');
    } finally {
      setBusyIso(null);
    }
  };

  const counts = useMemo(() => {
    const acc = { open: 0, waitlist: 0, unavailable: 0 };
    rows.forEach(r => { acc[r.status] = (acc[r.status] || 0) + 1; });
    return acc;
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter(r => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (!needle) return true;
      return (
        (r.name || '').toLowerCase().includes(needle) ||
        (r.iso_code || '').toLowerCase().includes(needle) ||
        (r.iso3_code || '').toLowerCase().includes(needle)
      );
    });
  }, [rows, q, statusFilter]);

  if (!isPlatformAdmin) {
    return (
      <DashboardLayout>
        <Card className="max-w-lg mt-10 mx-auto">
          <CardHeader><CardTitle>Supported Countries</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-[#475569]">This page is restricted to Platform Admins.</p>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="supported-countries-page">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[#0F172A] flex items-center gap-2">
              <Globe className="w-6 h-6 text-[#1A3E6F]" />
              Supported Countries
            </h1>
            <p className="text-sm text-[#64748B] mt-0.5">
              Master list controlling where VOX accepts new registrations, branches and operational addresses.
              Changes never mutate historical records.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-[#475569]">
            <Shield className="w-4 h-4" /> Platform Admin only
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {['open','waitlist','unavailable'].map(k => (
            <Card key={k} className="border-[#E2E8F0]">
              <CardContent className="p-4">
                <div className="text-xs uppercase tracking-wide text-[#64748B]">{STATUS_META[k].label}</div>
                <div className="text-2xl font-bold text-[#0F172A] mt-1" data-testid={`count-${k}`}>{counts[k] || 0}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                {['all','open','waitlist','unavailable'].map(s => (
                  <button
                    key={s}
                    type="button"
                    className={`px-3 py-1 text-xs font-semibold rounded-md border transition-colors ${
                      statusFilter === s
                        ? 'bg-[#1A3E6F] text-white border-[#1A3E6F]'
                        : 'bg-white text-[#475569] border-[#CBD5E1] hover:border-[#94A3B8]'
                    }`}
                    onClick={() => setStatusFilter(s)}
                    data-testid={`filter-${s}`}
                  >
                    {s === 'all' ? 'All' : STATUS_META[s].label}
                  </button>
                ))}
              </div>
              <Input
                placeholder="Search by country name or ISO code…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="max-w-xs"
                data-testid="country-search"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Country</TableHead>
                    <TableHead>ISO</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Note</TableHead>
                    <TableHead>Last changed</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading && (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#64748B]">Loading…</TableCell></TableRow>
                  )}
                  {!loading && filtered.map(r => (
                    <TableRow key={r.iso_code} data-testid={`row-${r.iso_code}`}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell className="text-xs text-[#64748B]">{r.iso_code} · {r.iso3_code}</TableCell>
                      <TableCell>
                        <span className={`inline-block px-2 py-0.5 text-xs font-semibold border rounded ${STATUS_META[r.status]?.cls || ''}`}>
                          {STATUS_META[r.status]?.label || r.status}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs text-[#475569] max-w-xs truncate">{r.note || '—'}</TableCell>
                      <TableCell className="text-xs text-[#64748B]">
                        {r.last_changed_at ? new Date(r.last_changed_at).toLocaleDateString() : '—'}
                        <div className="text-[10px]">{r.last_changed_by || ''}</div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-1">
                          {['open','waitlist','unavailable'].filter(s => s !== r.status).map(next => (
                            <Button
                              key={next}
                              size="sm"
                              variant="outline"
                              disabled={busyIso === r.iso_code}
                              onClick={() => changeStatus(r.iso_code, next)}
                              data-testid={`set-${r.iso_code}-${next}`}
                            >
                              → {STATUS_META[next].label}
                            </Button>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!loading && !filtered.length && (
                    <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#64748B]">No countries match this filter.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
